/**
 * TanStack Query hooks for the generic Entities admin feature.
 *
 * The OpenAPI surface for this module ships with `Record<string, never>`
 * payloads (record bodies) — i.e. unknown shape. We deliberately treat
 * incoming records as `Record<string, unknown>` and rely on the metadata
 * endpoint to drive the form rendering. Outbound payloads cast through
 * `as never` because `openapi-fetch` types `requestBody` as
 * `Record<string, never>`. TODO: drop casts when the backend publishes
 * proper schemas for the dynamic record shape.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

export type EntityListItem = components['schemas']['EntityListItem'];
export type EntityMetadataResponse = components['schemas']['EntityMetadataResponse'];
type EntityAttribute = NonNullable<EntityMetadataResponse['attributes']>[number];

/** Generic record shape — entities have arbitrary keys, so we lean on `unknown`. */
export type EntityRecord = Record<string, unknown>;

export interface EntityListFilters {
  name: string;
  page: number;
  size: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}

export interface EntityPage {
  content: EntityRecord[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export const entitiesKeys = {
  all: ['entities'] as const,
  index: () => ['entities', 'index'] as const,
  metadata: (name: string) => ['entities', 'metadata', name] as const,
  list: (filters: EntityListFilters) => ['entities', 'list', filters] as const,
  detail: (name: string, id: string) => ['entities', 'detail', name, id] as const,
};

export function useEntitiesList(): UseQueryResult<EntityListItem[], Error> {
  return useQuery<EntityListItem[], Error>({
    queryKey: entitiesKeys.index(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/entities');
      if (error) throw error as Error;
      return data ?? [];
    },
  });
}

export function useEntityMetadata(
  name: string | undefined,
): UseQueryResult<EntityMetadataResponse, Error> {
  return useQuery<EntityMetadataResponse, Error>({
    queryKey: entitiesKeys.metadata(name ?? ''),
    enabled: Boolean(name),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/entities/{name}/metadata', {
        params: { path: { name: name ?? '' } },
      });
      if (error) throw error as Error;
      return data as EntityMetadataResponse;
    },
  });
}

export function useEntityRecords(
  filters: EntityListFilters,
): UseQueryResult<EntityPage, Error> {
  return useQuery<EntityPage, Error>({
    queryKey: entitiesKeys.list(filters),
    enabled: Boolean(filters.name),
    queryFn: async () => {
      const query: { page?: number; size?: number; sort?: string; dir?: 'asc' | 'desc' } = {
        page: filters.page,
        size: filters.size,
      };
      if (filters.sort) query.sort = filters.sort;
      if (filters.dir) query.dir = filters.dir;
      const { data, error } = await api.GET('/api/v1/admin/entities/{name}', {
        params: {
          path: { name: filters.name },
          query,
        },
      });
      if (error) throw error as Error;
      // PagedResult.content is `Record<string, never>[]` in the generated
      // types; cast through `unknown` to relax to our generic record shape.
      const raw = data as unknown as {
        content?: EntityRecord[];
        page?: number;
        size?: number;
        totalElements?: number;
        totalPages?: number;
      };
      return {
        content: raw.content ?? [],
        page: raw.page ?? filters.page,
        size: raw.size ?? filters.size,
        totalElements: raw.totalElements ?? 0,
        totalPages: raw.totalPages ?? 0,
      };
    },
  });
}

export function useEntityRecord(
  name: string | undefined,
  id: string | undefined,
): UseQueryResult<EntityRecord, Error> {
  return useQuery<EntityRecord, Error>({
    queryKey: entitiesKeys.detail(name ?? '', id ?? ''),
    enabled: Boolean(name && id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/entities/{name}/{id}', {
        params: { path: { name: name ?? '', id: id ?? '' } },
      });
      if (error) throw error as Error;
      return (data ?? {}) as unknown as EntityRecord;
    },
  });
}

export function useUpdateEntityRecord(): UseMutationResult<
  EntityRecord,
  Error,
  { name: string; id: string; body: EntityRecord }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, id, body }) => {
      // openapi-fetch types requestBody as `Record<string, never>` for the
      // dynamic endpoint. Cast through `as never` until the backend
      // publishes a proper schema. TODO: drop cast.
      const { data, error } = await api.PUT('/api/v1/admin/entities/{name}/{id}', {
        params: { path: { name, id } },
        body: body as never,
      });
      if (error) throw error as Error;
      return (data ?? {}) as unknown as EntityRecord;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: entitiesKeys.all });
      void queryClient.invalidateQueries({
        queryKey: entitiesKeys.detail(vars.name, vars.id),
      });
    },
  });
}

export function useDeleteEntityRecord(): UseMutationResult<
  void,
  Error,
  { name: string; id: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, id }) => {
      const { error } = await api.DELETE('/api/v1/admin/entities/{name}/{id}', {
        params: { path: { name, id } },
      });
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: entitiesKeys.all });
    },
  });
}

/**
 * Field metadata normalized for the renderer. We map the backend
 * `javaType` (e.g. `java.lang.String`, `java.util.UUID`) onto a small
 * closed set of UI types — anything we cannot recognise falls back to
 * `string` for safety.
 */
export type FieldKind =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'ref'
  | 'blob'
  | 'json';

export interface NormalizedField {
  name: string;
  label: string;
  kind: FieldKind;
  required: boolean;
  readonly: boolean;
  isId: boolean;
  enumValues: string[];
  refEntity: string | undefined;
}

const NUMERIC_HINTS = ['int', 'long', 'short', 'byte', 'double', 'float', 'bigdecimal', 'number'];
const DATE_ONLY_HINTS = ['localdate'];
const DATE_TIME_HINTS = ['instant', 'localdatetime', 'offsetdatetime', 'zoneddatetime', 'date', 'timestamp'];
const BLOB_HINTS = ['byte[]', 'blob'];
const JSON_HINTS = ['map', 'jsonnode', 'list'];

/** Best-effort mapping of a raw javaType into a UI-friendly `FieldKind`. */
function inferKind(attr: EntityAttribute): FieldKind {
  if (attr.relationType && attr.relationType.length > 0) return 'ref';
  if (attr.enumValues && attr.enumValues.length > 0) return 'enum';
  const raw = (attr.javaType ?? '').toLowerCase();
  if (raw.endsWith('boolean')) return 'boolean';
  if (DATE_ONLY_HINTS.some((h) => raw.includes(h))) return 'date';
  if (DATE_TIME_HINTS.some((h) => raw.includes(h))) return 'datetime';
  if (BLOB_HINTS.some((h) => raw.includes(h))) return 'blob';
  if (JSON_HINTS.some((h) => raw.includes(h))) return 'json';
  if (NUMERIC_HINTS.some((h) => raw.includes(h))) return 'number';
  return 'string';
}

const humanize = (name: string): string =>
  name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function normalizeFields(
  metadata: EntityMetadataResponse | undefined,
): NormalizedField[] {
  const attrs = metadata?.attributes ?? [];
  return attrs
    .filter((a): a is EntityAttribute & { name: string } => Boolean(a.name))
    .map((a) => ({
      name: a.name,
      label: humanize(a.name),
      kind: inferKind(a),
      required: false,
      readonly: a.isReadOnly === true || a.isId === true,
      isId: a.isId === true,
      enumValues: a.enumValues ?? [],
      refEntity: a.targetEntity,
    }));
}
